
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@heroui/react";
import { trackException, trackInfo, LogLevel } from "../../../shared/services/appInsights";
import { AppInsightsActions, AppInsightsComponents, AppInsightsErrorTypes } from "../../../shared/services/appInsightsConstants";

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [iframeError, setIframeError] = useState(false);

  // Track page visit on component mount
  useEffect(() => {
    trackInfo("Register page visited", {
      component: AppInsightsComponents.REGISTER,
      action: AppInsightsActions.PAGE_VISIT
    });
  }, []);

  const handleNavigation = (path: string, actionName: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    
    try {
      // Track navigation attempt
      trackInfo(`Register page navigation - ${actionName}`, {
        component: AppInsightsComponents.REGISTER,
        action: actionName === "forgot-password" ? AppInsightsActions.NAVIGATION_FORGOT_PASSWORD : AppInsightsActions.NAVIGATION_ALREADY_HAVE_ACCOUNT,
        targetPath: path
      });
      
      navigate(path);
    } catch (error) {
      // Track navigation failure
      trackException(
        error as Error,
        LogLevel.Error,
        {
          component: AppInsightsComponents.REGISTER,
          action: AppInsightsActions.NAVIGATION_FAILURE,
          targetPath: path,
          actionName,
          errorType: AppInsightsErrorTypes.UNEXPECTED_ERROR,
          errorMessage: (error as Error).message
        }
      );
      console.error(`Navigation failed to ${path}:`, error);
    }
  };

  // Handle iframe load events
  const handleIframeLoad = () => {
    trackInfo("Registration form loaded successfully", {
      component: AppInsightsComponents.REGISTER,
      action: AppInsightsActions.IFRAME_LOAD_SUCCESS,
      formUrl: "office-forms"
    });
  };

  const handleIframeError = () => {
    setIframeError(true);
    trackException(
      new Error("Registration form failed to load"),
      LogLevel.Error,
      {
        component: AppInsightsComponents.REGISTER,
        action: AppInsightsActions.IFRAME_LOAD_FAILURE,
        errorType: AppInsightsErrorTypes.EXTERNAL_SERVICE_ERROR,
        formUrl: "office-forms"
      }
    );
  };

  const handleCancelClick = () => {
    try {
      trackInfo("Register cancelled", {
        component: AppInsightsComponents.REGISTER,
        action: AppInsightsActions.CANCEL_BUTTON_CLICK
      });
      
      navigate("/account/login");
    } catch (error) {
      trackException(
        error as Error,
        LogLevel.Error,
        {
          component: AppInsightsComponents.REGISTER, 
          action: AppInsightsActions.CANCEL_FAILURE,
          errorType: AppInsightsErrorTypes.UNEXPECTED_ERROR,
          errorMessage: (error as Error).message
        }
      );
      console.error("Cancel navigation failed:", error);
    }
  };

  return (
    <div className="form col-span-6 md:col-span-3 lg:col-span-4">
      <div className="grid grid-cols-8">
        <div className="col-span-2 sm:col-span-2 md:col-span-2 lg:col-span-1 xl:col-span-1 2xl:col-span-1">
          <h6 className="whitespace-nowrap">Register</h6>
        </div>
        <div className="m-1.5 col-span-8 sm:col-span-8 md:col-span-8 lg:col-span-5 xl:col-span-4 2xl:col-span-3">
          <div className="space-y-4">
            {/* Registration Form Container */}
            <div className="border rounded-md overflow-hidden">
              {iframeError ? (
                <div className="p-8 text-center text-red-600">
                  <p>Registration form failed to load. Please try refreshing the page or contact MSI.</p>
                </div>
              ) : (
                <iframe
                  title="Register User"
                  width="100%"
                  height="720px"
                  src="https://forms.office.com/Pages/ResponsePage.aspx?id=GrfYpE3Yr0Ot2AxxYWwy8qSsijejhxlEl8HmGaUeHfRUOFdKOE5HVlpTNVhTQ0E5VUo3WEVYT1ZQQSQlQCN0PWcu&embed=true"
                  style={{ border: "none" }}
                  className="w-full"
                  onLoad={handleIframeLoad}
                  onError={handleIframeError}
                />
              )}
            </div>

            {/* Navigation Links */}
            <div className="flex w-full gap-4 items-center justify-end">
              <a
                className="navigate-link"
                onClick={handleNavigation("/account/forgot-password", "forgot-password")}
              >
                Forgot password?
              </a>

              <Button
                size="md"
                className="rounded-none"
                color="default"
                variant="bordered"
                onPress={handleCancelClick}
              >
                Cancel
              </Button>
            </div>

            <div className="text-sm text-gray-600 mt-4">
              <p>
                Already have an account?{" "}
                <a
                  className="navigate-link"
                  onClick={handleNavigation("/account/login", "already-have-account")}
                >
                  Sign in here
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Register;